import {
  type CaseAnswers,
  type CaseTypeConfig,
  type ConfigField,
  type ConfigFieldOption,
  caseTypeFields,
} from '@/modules/projects/stores/caseConfig'
import { formatCalendarDate } from '@/utils/date/calendarDate'

export const CASE_TYPE_CELL_KEY = 'case-type'
export const ACCOUNTS_SCOPE_CELL_KEY = 'accounts-scope'

// One editable value in the case details grid.
export interface CaseDetailCell {
  key: string
  label: string
  kind: ConfigField['type']
  storedValue: string
  displayValue: string
  options: ConfigFieldOption[]
  // Case type and accounts in scope always hold a value. Intake answers can be emptied.
  isRequired: boolean
}

// Intake labels arrive in Title Case. Card copy is sentence case; acronyms are left alone.
const inSentenceCase = (label: string) =>
  label.replace(/(?<=.)\b[A-Z][a-z]+\b/g, (word) => word.toLowerCase())

const optionLabel = (options: ConfigFieldOption[], storedValue: string) =>
  options.find((option) => option.key === storedValue)?.label ?? storedValue

const displayValue = (field: ConfigField, storedValue: string) => {
  if (field.type === 'date') {
    return formatCalendarDate(storedValue) || storedValue
  }
  if (field.type === 'select') {
    return optionLabel(field.options ?? [], storedValue)
  }
  return storedValue
}

const selectCell = (
  key: string,
  label: string,
  storedValue: string,
  options: ConfigFieldOption[],
): CaseDetailCell => ({
  key,
  label,
  kind: 'select',
  storedValue,
  displayValue: optionLabel(options, storedValue),
  options,
  isRequired: true,
})

const intakeCell = (field: ConfigField, answers: CaseAnswers) => {
  const storedValue = answers.caseDetails?.[field.key]?.trim() ?? ''
  return {
    key: field.key,
    label: inSentenceCase(field.label),
    kind: field.type,
    storedValue,
    displayValue: displayValue(field, storedValue),
    options: field.options ?? [],
    isRequired: false,
  }
}

export const caseDetailCells = (
  answers: CaseAnswers,
  config: CaseTypeConfig,
): CaseDetailCell[] => [
  selectCell(
    CASE_TYPE_CELL_KEY,
    'Case type',
    answers.caseType,
    config.caseTypes,
  ),
  selectCell(
    ACCOUNTS_SCOPE_CELL_KEY,
    'Accounts in scope',
    answers.caseAccountsType,
    config.accountTypes,
  ),
  ...caseTypeFields(config.caseTypes, answers.caseType).map((field) =>
    intakeCell(field, answers),
  ),
]

export interface CaseTypeChange {
  caseType: string
  caseTypeLabel: string
  currentCaseTypeLabel: string
  keptDetails: Record<string, string>
  droppedLabels: string[]
}

// Lukita rejects values for fields the new case type does not have, so those cannot be kept.
export const planCaseTypeChange = (
  answers: CaseAnswers,
  config: CaseTypeConfig,
  nextCaseType: string,
): CaseTypeChange => {
  const nextFieldKeys = new Set(
    caseTypeFields(config.caseTypes, nextCaseType).map((field) => field.key),
  )
  const answered = caseTypeFields(config.caseTypes, answers.caseType).filter(
    (field) => answers.caseDetails?.[field.key]?.trim(),
  )
  const kept = answered.filter((field) => nextFieldKeys.has(field.key))
  const dropped = answered.filter((field) => !nextFieldKeys.has(field.key))

  return {
    caseType: nextCaseType,
    caseTypeLabel: optionLabel(config.caseTypes, nextCaseType),
    currentCaseTypeLabel: optionLabel(config.caseTypes, answers.caseType),
    keptDetails: Object.fromEntries(
      kept.map((field) => [field.key, answers.caseDetails[field.key]]),
    ),
    droppedLabels: dropped.map((field) => inSentenceCase(field.label)),
  }
}
